import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EditFacilityProcessSheet } from '../-components/EditFacilityProcessSheet';
import { FacilityProcessesTable } from '../-components/FacilityProcessesTable';
import { adminFacilityProcessesQueryOptions } from '../-hooks/useAdminFacilityProcesses';
import { useFacilityProcessesPage } from '../-hooks/useFacilityProcessesPage';

const facilityProcessesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/facility-processes/(pages)/')(
  {
    validateSearch: facilityProcessesSearch,
    loaderDeps: ({ search }) => ({
      page: search.page ?? 1,
      limit: search.limit ?? 50,
      ...(search.sort !== undefined ? { sort: search.sort } : {}),
      ...(search.filters !== undefined ? { filters: search.filters } : {}),
    }),
    // The rejection is swallowed on purpose. The loader is a prefetch — its only
    // job is to have the list already warm when the component mounts. Letting it
    // reject hands the route to the router's error boundary, which replaces the
    // whole page; the table's own "Could not load…" banner then never renders,
    // and that banner is what stops a failed load from reading as an empty
    // system. The component's `useQuery` hits the same failure and surfaces it
    // inline. Verified by the Playwright spec, which asserts the banner.
    loader: ({ deps }) =>
      queryClient
        .ensureQueryData(adminFacilityProcessesQueryOptions(deps))
        .catch(() => undefined),
    component: FacilityProcessesPage,
  },
);

function FacilityProcessesPage() {
  const page = useFacilityProcessesPage();

  return (
    <PageContainer>
      <FacilityProcessesTable page={page} />
      <EditFacilityProcessSheet page={page} />
    </PageContainer>
  );
}
